import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslations } from '../hooks/useTranslations';

const courseTitleFallbacks = {
  intensive: 'Intensiivinen kurssi',
  super: 'Superkurssi',
  superGuarantee: 'Super-Takuukurssi',
  applicationTravel: 'Hakemuspalvelu + matka',
  application: 'Hakemuspalvelu',
  knots: 'Knots & Suture',
};

const educationFallbacks = {
  vocational: 'Ammattikoulu',
  upperSecondary: 'Lukio',
  matriculation: 'Ylioppilas',
};

const completionFallbacks = {
  yes: 'Kyllä',
  no: 'En',
};

const EnrollmentForm = ({ courseId, onEnrollmentSuccess }) => {
  const [enrollData, setEnrollData] = useState({
    firstName: '',
    surname: '',
    birthday: '',
    email: '',
    phone: '',
    education: '',
    hasCompletedEducation: '',
  });
  const [showEducationOptions, setShowEducationOptions] = useState(false);
  const [showDegreeOptions, setShowDegreeOptions] = useState(false);
  const [showCompletionDateInput, setShowCompletionDateInput] = useState(false);
  const [showGraduationDateInput, setShowGraduationDateInput] = useState(false);
  const [completionDateValue, setCompletionDateValue] = useState('');
  const [graduationDateValue, setGraduationDateValue] = useState('');
  const [successMessage, setSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [milloinDropdownErrorMessage, setMilloinDropdownErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslations();

  if (!courseId) {
    return null;
  }

  const enrollUrl = process.env.REACT_APP_API_URL_ENROLL;

  const handleCloseForm = () => {
    onEnrollmentSuccess();
  };

  const handleSelectEducation = (optionId) => {
    setEnrollData({ ...enrollData, education: optionId });
    setShowEducationOptions(false);
    setErrorMessage('');
  };

  const handleSelectDegree = (optionId) => {
    setEnrollData({ ...enrollData, hasCompletedEducation: optionId });
    setShowDegreeOptions(false);
    setMilloinDropdownErrorMessage('');

    if (optionId === 'yes') {
      setShowCompletionDateInput(true);
      setShowGraduationDateInput(false);
    } else {
      setShowCompletionDateInput(false);
      setShowGraduationDateInput(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!enrollData.education) {
      setErrorMessage(t('enrollment.errors.selectEducation', 'Valitse tutkinto.'));
      setTimeout(() => setErrorMessage(''), 3000);
      setIsSubmitting(false);
      return;
    }

    if (!enrollData.hasCompletedEducation) {
      setMilloinDropdownErrorMessage(t('enrollment.errors.selectCompletion', 'Valitse vaihtoehto "Kyllä" tai "En".'));
      setTimeout(() => setMilloinDropdownErrorMessage(''), 3000);
      setIsSubmitting(false);
      return;
    }

    const fallbackCourseTitle = courseTitleFallbacks[courseId] || courseId;
    const payloadEducation = educationFallbacks[enrollData.education] || enrollData.education;

    const data = {
      name: enrollData.firstName,
      surname: enrollData.surname,
      birthday: enrollData.birthday,
      email: enrollData.email,
      phone: enrollData.phone,
      tutkinto: payloadEducation,
      milloin: showCompletionDateInput ? completionDateValue : graduationDateValue,
      courseTitle: fallbackCourseTitle,
    };

    try {
      const response = await fetch(enrollUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.status === 200) {
        setSuccessMessage(true);
        setEnrollData({
          firstName: '',
          surname: '',
          birthday: '',
          email: '',
          phone: '',
          education: '',
          hasCompletedEducation: '',
        });
        setShowCompletionDateInput(false);
        setShowGraduationDateInput(false);
        setCompletionDateValue('');
        setGraduationDateValue('');
        setTimeout(() => setSuccessMessage(false), 3000);
      }
    } catch (error) {
      console.error(error);
    }

    setIsSubmitting(false);
  };

  const displayedEducation = enrollData.education
    ? t(`enrollment.form.options.${enrollData.education}`, educationFallbacks[enrollData.education])
    : '';

  const displayedCompletion = enrollData.hasCompletedEducation
    ? t(`enrollment.form.choice.${enrollData.hasCompletedEducation}`, completionFallbacks[enrollData.hasCompletedEducation])
    : '';

  const courseTitle = t(`coursesPage.cards.${courseId}.title`, courseTitleFallbacks[courseId] || courseId);

  return (
    <div className="enrollment-overlay">
      <div className="enrollment-form">
        <FontAwesomeIcon
          icon={faTimes}
          className="close-button"
          onClick={handleCloseForm}
          aria-label={t('enrollment.actions.close', 'Sulje lomake')}
        />
        <h1>{t('enrollment.heading', 'Ilmoittautuminen')}</h1>
        <h2>{courseTitle}</h2>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder={t('enrollment.form.firstName', 'Etunimi')}
            value={enrollData.firstName}
            onChange={(e) => setEnrollData({ ...enrollData, firstName: e.target.value })}
            required
          />
          <input
            type="text"
            placeholder={t('enrollment.form.lastName', 'Sukunimi')}
            value={enrollData.surname}
            onChange={(e) => setEnrollData({ ...enrollData, surname: e.target.value })}
            required
          />
          <input
            type="text"
            placeholder={t('enrollment.form.birthdate', 'Syntymäpäivä (10.06.1999)')}
            value={enrollData.birthday}
            onChange={(e) => setEnrollData({ ...enrollData, birthday: e.target.value })}
            required
          />
          <input
            type="email"
            placeholder={t('enrollment.form.email', 'Sähköposti')}
            value={enrollData.email}
            onChange={(e) => setEnrollData({ ...enrollData, email: e.target.value })}
            required
          />
          <input
            type="tel"
            placeholder={t('enrollment.form.phone', 'Puhelin')}
            value={enrollData.phone}
            onChange={(e) => setEnrollData({ ...enrollData, phone: e.target.value })}
            required
          />
          <div className="tutkinto-dropdown">
            <input
              type="text"
              placeholder={
                enrollData.education
                  ? t('enrollment.form.qualifications', 'Tutkinnot')
                  : t('enrollment.form.qualificationsRequired', 'Tutkinnot*')
              }
              value={displayedEducation}
              onClick={() => setShowEducationOptions((prev) => !prev)}
              readOnly
            />
            {showEducationOptions && (
              <div className="options-list">
                {Object.keys(educationFallbacks).map((optionId) => (
                  <div key={optionId} className="option" onClick={() => handleSelectEducation(optionId)}>
                    {t(`enrollment.form.options.${optionId}`, educationFallbacks[optionId])}
                  </div>
                ))}
              </div>
            )}
          </div>
          {errorMessage && <div className="error-message">{errorMessage}</div>}

          {enrollData.education && (
            <div className="milloin-dropdown">
              <input
                type="text"
                placeholder={t('enrollment.form.hasCompleted', 'Oletko suorittanut tutkinnon?')}
                value={displayedCompletion}
                onClick={() => setShowDegreeOptions((prev) => !prev)}
                readOnly
              />
              {showDegreeOptions && (
                <div className="options-list">
                  {(['yes', 'no']).map((optionId) => (
                    <div key={optionId} className="option" onClick={() => handleSelectDegree(optionId)}>
                      {t(`enrollment.form.choice.${optionId}`, completionFallbacks[optionId])}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {milloinDropdownErrorMessage && <div className="error-message">{milloinDropdownErrorMessage}</div>}

          {showCompletionDateInput && (
            <input
              type="text"
              placeholder={t('enrollment.form.completionDate', 'Milloin suoritit? (20.09.2022)')}
              value={completionDateValue}
              onChange={(e) => setCompletionDateValue(e.target.value)}
              required
            />
          )}
          {showGraduationDateInput && (
            <input
              type="text"
              placeholder={t('enrollment.form.graduationDate', 'Milloin valmistut? (20.09.2024)')}
              value={graduationDateValue}
              onChange={(e) => setGraduationDateValue(e.target.value)}
              required
            />
          )}
          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? t('enrollment.form.buttons.sending', 'Lähetetään...') : t('enrollment.form.buttons.submit', 'Lähetä')}
          </button>
        </form>
        {successMessage && (
          <div className="success-message">{t('enrollment.form.success', 'Ilmoittautuminen hyväksytty')}</div>
        )}
      </div>
    </div>
  );
};

export default EnrollmentForm;
